import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { admin } = useAuth();
  const [lastMessage, setLastMessage] = useState(null);
  const [refreshTriggers, setRefreshTriggers] = useState({
    leads: 0,
    proposals: 0,
    agreements: 0,
    invoices: 0
  });

  const handleMessage = useCallback((data) => {
    console.log('🔔 WebSocket message received:', data);
    setLastMessage(data);

    // Handle different message types
    switch (data.type) {
      case 'connection':
        console.log('✅ Real-time connection established');
        break;

      case 'lead_stage_change':
        console.log('📊 Lead stage change event received');
        toast.success('Lead Stage Updated', {
          description: `Lead moved to ${data.data.new_stage}`,
          duration: 3000
        });
        // Trigger refresh
        setRefreshTriggers(prev => ({...prev, leads: prev.leads + 1}));
        console.log('🔄 Lead refresh triggered (stage change)');
        break;

      case 'lead_assignment':
        console.log('👤 Lead assignment event received');
        toast.info('New Lead Assigned', {
          description: `${data.data.lead_name} assigned to you by ${data.data.assigned_by}`,
          duration: 5000
        });
        // Trigger refresh
        setRefreshTriggers(prev => ({...prev, leads: prev.leads + 1}));
        console.log('🔄 Lead refresh triggered (assignment)');
        break;

      case 'email_response':
        console.log('📧 Email response event received');
        toast.info('New Email Response', {
          description: `From: ${data.data.email_from}\nSubject: ${data.data.email_subject}`,
          duration: 5000
        });
        // Trigger refresh
        setRefreshTriggers(prev => ({...prev, leads: prev.leads + 1}));
        console.log('🔄 Lead refresh triggered (email)');
        break;

      case 'data_refresh':
        console.log('🔄 Data refresh event received for:', data.data.refresh);
        // Silently refresh data
        const refreshType = data.data.refresh;
        setRefreshTriggers(prev => ({...prev, [refreshType]: prev[refreshType] + 1}));
        console.log('✅ Refresh trigger updated for:', refreshType);
        break;

      default:
        console.log('⚠️ Unknown message type:', data.type);
    }
  }, []);

  // Get token from localStorage
  const token = localStorage.getItem('admin_token');

  // Connect to WebSocket only if admin is logged in
  useWebSocket(admin ? handleMessage : null, token);

  const value = {
    lastMessage,
    refreshTriggers
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
